/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Account } from '@carbon/react/icons';
import {
  CodeSnippet,
  CodeSnippetSkeleton,
  FormLabel,
  ListItem,
  UnorderedList
} from 'carbon-components-react';
import _ from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useMount } from 'react-use';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  isSubscribeIntegrationLoading,
  selectDraftIntegration
} from '../../../store/channels/selectors';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { TwilioAuth } from '../../twilio-auth/TwilioAuth';
import { links } from '../links';
import { FIELDS } from '../schema';
import { BasicSetup as messages } from './messages';

export const BasicSetup = ({
  assistantId,
  environmentId,
  readOnly
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, isInvalidOrEmpty } = useFormHelpers(formMethods);
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const isSubscribeLoading = useAppSelector(isSubscribeIntegrationLoading);

  useMount(() => {
    if (
      isInvalidOrEmpty(FIELDS.WEBHOOK_URL) &&
      !_.isNil(assistantId) &&
      !_.isNil(draftIntegration?.integration_id)
    ) {
      subscribeForWebhookUrl(
        assistantId,
        environmentId,
        draftIntegration,
        {
          account_sid: getFieldValue(FIELDS.ACCOUNT_SID),
          auth_token: getFieldValue(FIELDS.TWILIO_AUTHENTICATION_TOKEN)
        },
        FIELDS.WEBHOOK_URL
      );
    }
  });

  return (
    <TabContent
      ImageComponent={() => <Account size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="TabContent__body-content">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreWhatsAppSetupNewIA
                : links.learnMoreWhatsAppSetupLink
            }
            {...messages.subtitle}
          />
        </div>
        <TwilioAuth
          readOnly={readOnly}
          accountSidField={FIELDS.ACCOUNT_SID}
          authTokenField={FIELDS.TWILIO_AUTHENTICATION_TOKEN}
          isAccountSidPublic
        />
        <div className="TabContent__field-container">
          <h4>
            <FormattedMessage {...messages.webhookHeader} />
          </h4>
          <div className="TabContent__spacer8" />
          <div className="TabContent__subtitle">
            <FormattedMessage {...messages.webhookDescription} />
          </div>
          <FormLabel className="TabContent__field-label">
            <FormattedMessage {...messages.webhookLabel} />
          </FormLabel>
          {_.isNil(draftIntegration?.integration_id) || isSubscribeLoading ? (
            <CodeSnippetSkeleton />
          ) : (
            <CodeSnippet
              id={FIELDS.WEBHOOK_URL}
              copyButtonDescription={t(sharedMessages.copyToClipboard)}
              copyLabel={t(sharedMessages.copyToClipboard)}
              feedback={t(sharedMessages.copied)}
              onClick={() => {
                copyContentToClipboard(getFieldValue(FIELDS.WEBHOOK_URL));
                ctaEvents.whatsAppWebhookCopied();
              }}
              type="single">
              {getFieldValue(FIELDS.WEBHOOK_URL)}
            </CodeSnippet>
          )}
        </div>
        <div className="TabContent__instructions">
          <h4>
            <FormattedMessage {...messages.instructions} />
          </h4>
          <FormLabel className="TabContent__field-label">
            <FormattedMessage {...messages.instructionsDescription} />
          </FormLabel>
          <div className="TabContent__field">
            <UnorderedList className="TabContent__subtitle">
              <ListItem>
                <FormattedMessage {...messages.instructionsItemOne} />
              </ListItem>
              <ListItem>
                <FormattedMessage {...messages.instructionsItemTwo} />
              </ListItem>
            </UnorderedList>
          </div>
          <div className="TabContent__description">
            <FormattedMessageWithLink
              url={links.TWILIO_API_LINK}
              {...messages.instructionsLinkTwilio}
            />
          </div>
          <div className="TabContent__description">
            <FormattedMessageWithLink
              url={links.FACEBOOK_MESSENGER_MANAGER_ID_LINK}
              {...messages.instructionsLinkFacebook}
            />
          </div>
        </div>
      </div>
    </TabContent>
  );
};
